import { useContext, useState } from "react";
import {
  seedFactfindOntology,
  seedOntology,
} from "../../functions/knowledgeGraphApi";
import { DataContext } from "../../contexts/DataProvider";
import { useAlert } from "../../functions/useAlert";
import {
  fetchOrganisations as fetchOrgList,
  createOrganisation,
  updateOrganisation,
  deleteOrganisation,
} from "../../services/organisations.service";
import {
  EMAIL_REGEX,
  ABN_REGEX,
  WEBSITE_REGEX,
  VAL_MSG,
  isValidAuPhone,
} from "../utils/validation";

const DEFAULT_ORG = {
  name: "",
  abn: "",
  afslNumber: "",
  website: "",
  billingContactName: "",
  billingContactEmail: "",
  billingContactPhone: "",
  billingContactMobile: "",
  billingAddress: {
    line1: "",
    line2: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
  },
  status: "active",
  twoFactorEnabled: false,
  isTrialing: false,
  hierarchyRole: "standalone",
  parentOrganisationId: "",
};

interface UseOrganisationsOptions {
  selectedOrganization: string;
  organisationList: any[];
  onOrganisationsLoaded: (orgs: any[]) => void;
  onStorageProviderChange: (provider: string) => void;
}

export function useOrganisations({
  selectedOrganization,
  organisationList,
  onOrganisationsLoaded,
  onStorageProviderChange,
}: UseOrganisationsOptions) {
  const { login }: any = useContext(DataContext);
  const isGlobalAdmin = login?.data?.role === "global admin";
  const [setAlert] = useAlert();

  const [editingData, setEditingData] = useState<any[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [editData, setEditData] = useState<any>(null);
  const [openRow, setOpenRow] = useState<number | null>(null);
  const [organisation, setOrganisation] = useState<any>({ ...DEFAULT_ORG });
  const [orgValidationErrors, setOrgValidationErrors] = useState<
    Record<string, string>
  >({});

  // Delete org confirmation modal
  const [deleteOrgConfirmOpen, setDeleteOrgConfirmOpen] =
    useState<boolean>(false);
  const [deleteOrgId, setDeleteOrgId] = useState<string | null>(null);
  const [deleteOrgName, setDeleteOrgName] = useState<string>("");
  const [deleteOrgConfirmText, setDeleteOrgConfirmText] = useState<string>("");

  // "New Organisation" button menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openButton = Boolean(anchorEl);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (type?: string) => {
    setAnchorEl(null);
    if (type) {
      console.log(`Selected: ${type}`);
    }
  };

  const handleClickOrganisation = () => {
    setOpenModal(true);
    setEditData(null);
    handleCloseMenu();
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditData(null);
    setOrganisation({ ...DEFAULT_ORG });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setOrganisation((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleInputChange = (index: number, field: string, value: any) => {
    const updated = [...editingData];
    updated[index] = { ...updated[index], [field]: value };
    setEditingData(updated);
  };

  const handleExpand = (index: number) => {
    setOpenRow(openRow === index ? null : index);
  };

  const handleCancel = () => {
    setOpenRow(null);
  };

  const fetchOrganisations = async () => {
    try {
      const data: any = await fetchOrgList();
      onOrganisationsLoaded(data.organisations);
      setEditingData(data.organisations);

      if (selectedOrganization && selectedOrganization !== "All Organisation") {
        const org = data.organisations.find(
          (o: any) => o._id === selectedOrganization,
        );
        if (org) {
          const provider = org.storageSettings?.provider || "AZURE";
          onStorageProviderChange(provider);
        }
      }
    } catch (error) {
      console.error("Error fetching organisations:", error);
    }
  };

  const handleSubmit = async () => {
    const errors: Record<string, string> = {};
    const requiredFields = [
      "name",
      "abn",
      "website",
      "billingContactName",
      "billingContactEmail",
      "billingContactPhone",
      "billingContactMobile",
      "billingAddress",
      "status",
    ];
    requiredFields.forEach((field) => {
      if (!organisation[field]) errors[field] = VAL_MSG.required;
    });

    if (organisation.name && !/[a-zA-Z]/.test(organisation.name.trim())) {
      errors.name = "Organisation name cannot be purely numerical";
    }
    if (
      organisation.billingContactName &&
      !/[a-zA-Z]/.test(organisation.billingContactName.trim())
    ) {
      errors.billingContactName =
        "Billing contact name cannot be purely numerical";
    }
    if (
      organisation.billingContactEmail &&
      !EMAIL_REGEX.test(organisation.billingContactEmail)
    ) {
      errors.billingContactEmail = VAL_MSG.email;
    }
    if (
      organisation.billingContactPhone &&
      !isValidAuPhone(organisation.billingContactPhone)
    ) {
      errors.billingContactPhone = VAL_MSG.phone;
    }
    if (
      organisation.billingContactMobile &&
      !isValidAuPhone(organisation.billingContactMobile)
    ) {
      errors.billingContactMobile = VAL_MSG.mobile;
    }
    if (
      organisation.abn &&
      !ABN_REGEX.test(organisation.abn.replace(/\s/g, ""))
    ) {
      errors.abn = VAL_MSG.abn;
    }
    if (
      organisation.website &&
      !WEBSITE_REGEX.test(organisation.website.trim())
    ) {
      errors.website = "Please enter a valid website (e.g. www.example.com)";
    }

    setOrgValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      if (editData?._id) {
        await updateOrganisation(editData._id, organisation);
      } else {
        const newOrg: any = await createOrganisation(organisation);
        const orgId = newOrg?._id || newOrg?.id;
        seedOntology().catch((err) =>
          console.error("seedOntology failed:", err),
        );
        if (orgId) {
          seedFactfindOntology(orgId).catch((err) =>
            console.error("seedFactfindOntology failed:", err),
          );
        }
      }
      setAlert("Organisation created successfully!", "success");
      fetchOrganisations();
      handleCloseModal();
    } catch (error: any) {
      setAlert(
        error?.data?.error || "Error saving organisation. Please try again.",
        "error",
      );
    }
  };

  const handleSave = async (index: number) => {
    try {
      const orgToUpdate = editingData[index];
      if (!orgToUpdate.name?.trim()) {
        setAlert("Organisation name is required", "error");
        return;
      }
      if (!/[a-zA-Z]/.test(orgToUpdate.name.trim())) {
        setAlert("Organisation name cannot be purely numerical", "error");
        return;
      }
      if (
        orgToUpdate.billingContactName &&
        !/[a-zA-Z]/.test(orgToUpdate.billingContactName)
      ) {
        setAlert("Billing contact name cannot be purely numerical", "error");
        return;
      }
      if (
        orgToUpdate.billingContactEmail &&
        !EMAIL_REGEX.test(orgToUpdate.billingContactEmail)
      ) {
        setAlert(VAL_MSG.email, "error");
        return;
      }
      if (
        orgToUpdate.billingContactPhone &&
        !isValidAuPhone(orgToUpdate.billingContactPhone)
      ) {
        setAlert(
          "Enter a valid phone number (e.g. +61412345678 or 0412345678)",
          "error",
        );
        return;
      }
      if (
        orgToUpdate.billingContactMobile &&
        !isValidAuPhone(orgToUpdate.billingContactMobile)
      ) {
        setAlert(
          "Enter a valid mobile number (e.g. +61412345678 or 0412345678)",
          "error",
        );
        return;
      }
      if (
        orgToUpdate.abn &&
        !ABN_REGEX.test(orgToUpdate.abn.replace(/\s/g, ""))
      ) {
        setAlert(VAL_MSG.abn, "error");
        return;
      }
      if (
        orgToUpdate.website &&
        !WEBSITE_REGEX.test(orgToUpdate.website.trim())
      ) {
        setAlert(
          "Please enter a valid website (e.g. www.example.com)",
          "error",
        );
        return;
      }
      const payload = {
        ...orgToUpdate,
        isBillingExempt: orgToUpdate.isBillingExempt || false,
      };
      await updateOrganisation(orgToUpdate._id, payload);
      setAlert("Organisation edited successfully!", "success");
      fetchOrganisations();
      setOpenRow(null);
    } catch (err) {
      console.error("Error updating organisation:", err);
    }
  };

  const handleReassignOrganisation = async (
    organisationId: string,
    parentOrganisationId: string,
  ) => {
    try {
      await updateOrganisation(organisationId, {
        parentOrganisationId: parentOrganisationId || null,
      });
      setAlert(
        parentOrganisationId
          ? "Organisation reassigned successfully!"
          : "Organisation is now standalone!",
        "success",
      );
      await fetchOrganisations();
    } catch (error: any) {
      setAlert(
        error?.data?.error ||
          "Unable to reassign the organisation. Please try again.",
        "error",
      );
      throw error;
    }
  };

  const handleDelete = (id: string) => {
    if (!isGlobalAdmin) {
      setAlert("Only global administrators can delete organizations!", "error");
      return;
    }
    const orgToDelete = (organisationList || []).find(
      (org: any) => org._id === id,
    );
    if (!orgToDelete) {
      setAlert("Organization not found!", "error");
      return;
    }
    setDeleteOrgId(id);
    setDeleteOrgName(orgToDelete.name);
    setDeleteOrgConfirmText("");
    setDeleteOrgConfirmOpen(true);
  };

  const confirmDeleteOrganisation = async () => {
    if (!deleteOrgId || !deleteOrgName) return;
    if (deleteOrgConfirmText !== deleteOrgName.toUpperCase()) {
      setAlert(
        "Please type the organization name in full caps to confirm deletion",
        "error",
      );
      return;
    }
    try {
      await deleteOrganisation(deleteOrgId);
      setAlert("Organisation deleted successfully!", "success");
      fetchOrganisations();
    } catch (err) {
      console.error("Error deleting organisation:", err);
      setAlert("Error deleting organization", "error");
    } finally {
      setDeleteOrgConfirmOpen(false);
      setDeleteOrgId(null);
      setDeleteOrgName("");
      setDeleteOrgConfirmText("");
    }
  };

  const cancelDeleteOrganisation = () => {
    setDeleteOrgConfirmOpen(false);
    setDeleteOrgId(null);
    setDeleteOrgName("");
    setDeleteOrgConfirmText("");
  };

  return {
    editingData,
    setEditingData,
    openModal,
    setOpenModal,
    editData,
    setEditData,
    openRow,
    setOpenRow,
    organisation,
    setOrganisation,
    orgValidationErrors,
    setOrgValidationErrors,
    deleteOrgConfirmOpen,
    setDeleteOrgConfirmOpen,
    deleteOrgId,
    setDeleteOrgId,
    deleteOrgName,
    setDeleteOrgName,
    deleteOrgConfirmText,
    setDeleteOrgConfirmText,
    anchorEl,
    openButton,
    handleOpenMenu,
    handleCloseMenu,
    handleClick,
    handleClose,
    handleClickOrganisation,
    handleCloseModal,
    handleChange,
    handleInputChange,
    handleExpand,
    handleCancel,
    fetchOrganisations,
    handleSubmit,
    handleSave,
    handleReassignOrganisation,
    handleDelete,
    confirmDeleteOrganisation,
    cancelDeleteOrganisation,
  };
}
